import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ClosureDTO, getClosureDetailsById, getContractPlacementById } from 'wisestep-api';
import { FormWizard, WsTitleBanner } from 'wisestep-base';

import ClosureCandidateDetails from './components/ClosureCandidateDetails';
import ClosureCompensationDetails from './components/ClosureCompensationDetails';
import ClosureJobDetails from './components/ClosureJobDetails';
import ClosureRecruiterDetails from './components/ClosureRecruiterDetails';
import CreatePlacementForm from './components/CreatePlacementForm';
import { closurePayload } from './helper/payload';

type ClosureFormParams = {
  closureId: string;
  mode: string;
  step: string;
};

const ClosureForm = () => {
  const navigate = useNavigate();
  const location: any = useLocation();
  const params = useParams<ClosureFormParams>();
  const [closureDTO, setClosureDTO] = useState<ClosureDTO>(closurePayload);
  const [placementType, setPlacementType] = useState<string>('PERMANENT');

  const [getDirtyFields, setDirtyFields] = useState([]);

  const [isCandidateFromSubmission, setIscandidateFromSubmission] = useState(false);

  const [formWizardCurrentStep, setFormWizardCurrentStep] = useState(0);
  const [formWizardCurrentChildStep, setFormWizardCurrentChildStep] = useState(
    parseInt(params.step ?? '0')
  );

  const formWizardMenuItemClick = (id: number) => setFormWizardCurrentStep(id);
  const formWizardSubMenuItemClick = (id: number) => setFormWizardCurrentChildStep(id);
  const onFormWizardArrowClick = () => navigate('/placements');
  const onCancel = () => navigate('/placements');

  const closureId: string = params.closureId ?? '';
  const mode: string = params.mode ?? '';

  const menuItems = [
    {
      id: 0,
      label: 'Placement Information',
      onClick: () => formWizardMenuItemClick(0),
    },
  ];

  const menuItemsContract = [
    {
      id: 0,
      label: 'Contract Placement Information',
      subList: [
        {
          id: 0,
          label: 'Candidate Details',
          onClick: () => formWizardSubMenuItemClick(0),
        },
        {
          id: 1,
          label: 'Job Details',
          onClick: () => formWizardSubMenuItemClick(1),
        },
        {
          id: 2,
          label: 'Compensation Details',
          onClick: () => formWizardSubMenuItemClick(2),
        },
        {
          id: 3,
          label: 'Placement Enablers',
          onClick: () => formWizardSubMenuItemClick(3),
        },
      ],
      onClick: () => formWizardMenuItemClick(0),
    },
  ];

  useEffect(() => {
    if (location.state && location.state.placementType) {
      setPlacementType(location.state.placementType);
    } else {
      setPlacementType('PERMANENT');
    }
  }, []);

  useEffect(() => {
    const getClosureDetails = async (closureId: string) => {
      const closureResponse = await getClosureDetailsById(closureId);
      setClosureDTO(closureResponse.data);
    };
    const getContractClosureDetails = async (closureId: string) => {
      const closureResponse = await getContractPlacementById(closureId);
      setClosureDTO(closureResponse.data);
    };
    if (mode === 'edit') {
      if (placementType === 'PERMANENT') {
        getClosureDetails(closureId);
      } else {
        getContractClosureDetails(closureId);
      }
    }
  }, [closureId, mode, setClosureDTO, placementType]);

  return (
    <>
      {placementType === 'CONTRACT' && (
        <FormWizard
          menuItems={menuItemsContract}
          onArrowButtonClick={onFormWizardArrowClick}
          currentStep={formWizardCurrentStep}
          currentChildStep={formWizardCurrentChildStep}
          title="Placement List"
        >
          <WsTitleBanner title="Placement Details" backButton={false} />
          {formWizardCurrentStep == 0 &&
            formWizardCurrentChildStep == 0 &&
            (((mode === 'edit' && closureDTO.id) ?? false) || mode !== 'edit') && (
              <ClosureCandidateDetails
                formWizardSubMenuItemClick={formWizardSubMenuItemClick}
                setClosureDTO={setClosureDTO}
                currentClosureDTO={closureDTO}
                setDirtyFieldsValue={setDirtyFields}
                getDirtyFieldsValue={getDirtyFields}
                onCancel={onCancel}
                setCandidateFromSubmission={setIscandidateFromSubmission}
                placementType={placementType}
              />
            )}
          {formWizardCurrentStep == 0 &&
            formWizardCurrentChildStep == 1 &&
            (((mode === 'edit' && closureDTO.id) ?? false) || mode !== 'edit') && (
              <ClosureJobDetails
                formWizardSubMenuItemClick={formWizardSubMenuItemClick}
                setClosureDTO={setClosureDTO}
                currentClosureDTO={closureDTO}
                setDirtyFieldsValue={setDirtyFields}
                getDirtyFieldsValue={getDirtyFields}
                onCancel={onCancel}
              />
            )}
          {formWizardCurrentStep == 0 &&
            formWizardCurrentChildStep == 2 &&
            (((mode === 'edit' && closureDTO.id) ?? false) || mode !== 'edit') && (
              <ClosureCompensationDetails
                formWizardSubMenuItemClick={formWizardSubMenuItemClick}
                setClosureDTO={setClosureDTO}
                currentClosureDTO={closureDTO}
                setDirtyFieldsValue={setDirtyFields}
                getDirtyFieldsValue={getDirtyFields}
                onCancel={onCancel}
                placementType={placementType}
              />
            )}
          {formWizardCurrentStep == 0 &&
            formWizardCurrentChildStep == 3 &&
            (((mode === 'edit' && closureDTO.id) ?? false) || mode !== 'edit') && (
              <ClosureRecruiterDetails
                setClosureDTO={setClosureDTO}
                currentClosureDTO={closureDTO}
                setDirtyFieldsValue={setDirtyFields}
                getDirtyFieldsValue={getDirtyFields}
                onCancel={onCancel}
                getCandidateFromSubmissionStatus={isCandidateFromSubmission}
                placementType={placementType}
              />
            )}
        </FormWizard>
      )}
      {placementType === 'PERMANENT' && (
        <FormWizard
          menuItems={menuItems}
          onArrowButtonClick={onFormWizardArrowClick}
          currentStep={formWizardCurrentStep}
          title="Placement List"
        >
          <WsTitleBanner title="Placement Details" backButton={false} />
          {formWizardCurrentStep == 0 &&
            (((mode === 'edit' && closureDTO.id) ?? false) || mode !== 'edit') && (
              <CreatePlacementForm
                setClosureDTO={setClosureDTO}
                currentClosureDTO={closureDTO}
                onCancel={onCancel}
                getDirtyFieldsValue={getDirtyFields}
              />
            )}
        </FormWizard>
      )}
    </>
  );
};

export default ClosureForm;
